/**
 * Secure token storage (ported from ai-interviewer, typed).
 *
 * Strategy:
 * - Access token kept in memory (most secure) with a sessionStorage mirror so a
 *   page refresh survives without persisting to localStorage.
 * - Cleared on logout and when the tab closes (sessionStorage semantics).
 *
 * NEVER store tokens in localStorage and NEVER log them (standards §12.1/§13.3).
 */
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'dda_access_token',
} as const;

class TokenStorage {
  private accessToken: string | null = null;

  setAccessToken(token: string | null): void {
    this.accessToken = token;
    try {
      if (token) {
        sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
      } else {
        sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      }
    } catch {
      /* sessionStorage unavailable (private mode) — memory copy still works. */
    }
  }

  getAccessToken(): string | null {
    if (!this.accessToken) {
      try {
        this.accessToken = sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      } catch {
        this.accessToken = null;
      }
    }
    return this.accessToken;
  }

  hasToken(): boolean {
    return !!this.getAccessToken();
  }

  clear(): void {
    this.accessToken = null;
    try {
      sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    } catch {
      /* ignore */
    }
  }
}

export const tokenStorage = new TokenStorage();
export { TokenStorage, STORAGE_KEYS };
